/**
 * ContactQuickView Component
 * Hover card that displays contact information preview
 * Used in task lists to show contact details without navigating away
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getContact } from "../../services/contacts.service";
import type { UUID } from "../../types/common.types";

interface ContactQuickViewProps {
  contactId: UUID;
  children: React.ReactNode;
  disabled?: boolean;
  fromTaskId?: UUID; // Pass task ID to enable "Back to Task" navigation on contact detail
}

interface CardStyle {
  position: "fixed";
  top?: number;
  bottom?: number;
  left: number;
  zIndex: number;
}

const ContactQuickView: React.FC<ContactQuickViewProps> = ({
  contactId,
  children,
  disabled = false,
  fromTaskId,
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [cardStyle, setCardStyle] = useState<CardStyle | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch contact data on hover
  const { data: contact, isLoading } = useQuery({
    queryKey: ["contact-quickview", contactId],
    queryFn: () => getContact(contactId),
    enabled: isHovering && !disabled,
    staleTime: 30000, // Cache for 30 seconds
  });

  // Calculate card position using fixed positioning for reliable viewport handling
  const calculateCardPosition = useCallback(() => {
    if (!triggerRef.current) {
      return;
    }

    const rect = triggerRef.current.getBoundingClientRect();
    const cardHeight = 250; // Approximate card height
    const cardWidth = 320;
    const padding = 8; // Minimum padding from viewport edges

    // Calculate available space above and below
    const spaceBelow = window.innerHeight - rect.bottom - padding;
    const spaceAbove = rect.top - padding;

    // Determine vertical position - prefer below, use above if not enough space
    const showAbove = spaceBelow < cardHeight && spaceAbove > spaceBelow;

    // Calculate horizontal position, keeping card within viewport
    let left = rect.left;
    if (left + cardWidth > window.innerWidth - padding) {
      left = window.innerWidth - cardWidth - padding;
    }
    if (left < padding) {
      left = padding;
    }

    if (showAbove) {
      setCardStyle({
        position: "fixed",
        bottom: window.innerHeight - rect.top + 4,
        left,
        zIndex: 9999,
      });
    } else {
      setCardStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left,
        zIndex: 9999,
      });
    }
  }, []);

  // Handle mouse enter with delay
  const handleMouseEnter = useCallback(() => {
    if (disabled) {
      return;
    }

    // Cancel any pending hide
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    setIsHovering(true);

    // Only set timeout if card not already showing
    if (!showCard) {
      hoverTimeoutRef.current = setTimeout(() => {
        calculateCardPosition();
        setShowCard(true);
      }, 300); // 300ms delay before showing
    }
  }, [disabled, showCard, calculateCardPosition]);

  // Handle mouse leave with delay to allow moving to card
  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    // Delay hiding to allow mouse to move to card
    hideTimeoutRef.current = setTimeout(() => {
      setIsHovering(false);
      setShowCard(false);
    }, 150); // 150ms grace period to move to card
  }, []);

  // Handle mouse enter on card - cancel hide
  const handleCardMouseEnter = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  // Handle mouse leave on card - start hide
  const handleCardMouseLeave = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setIsHovering(false);
      setShowCard(false);
    }, 100);
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const formatMemberStatus = (
    status?: string,
  ): { label: string; color: string } => {
    if (!status) {
      return { label: "Unknown", color: "bg-gray-100 text-gray-600" };
    }
    const statusLower = status.toLowerCase();
    if (statusLower === "active") {
      return { label: "Active Member", color: "bg-green-100 text-green-800" };
    } else if (statusLower === "expired" || statusLower === "inactive") {
      return { label: "Expired Member", color: "bg-red-100 text-red-800" };
    } else if (statusLower === "pending") {
      return { label: "Pending", color: "bg-amber-100 text-amber-800" };
    }
    return { label: status, color: "bg-gray-100 text-gray-600" };
  };

  const formatLifecycleStage = (stage?: string): string => {
    if (!stage) {
      return "";
    }
    const labels: Record<string, string> = {
      lead: "Lead",
      prospect: "Prospect",
      customer: "Customer",
      partner: "Partner",
      inactive: "Inactive",
    };
    return labels[stage.toLowerCase()] || stage;
  };

  return (
    <div
      ref={triggerRef}
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}

      {/* Quick View Card - Using fixed positioning for reliable viewport handling */}
      {showCard && cardStyle && (
        <div
          ref={cardRef}
          className="w-80 bg-white border border-gray-200 rounded-lg shadow-xl p-4"
          style={{
            ...cardStyle,
            minHeight: "150px",
          }}
          onMouseEnter={handleCardMouseEnter}
          onMouseLeave={handleCardMouseLeave}
        >
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            </div>
          ) : contact ? (
            <div className="space-y-3">
              {/* Header */}
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h4 className="font-semibold text-gray-900">
                    {contact.first_name} {contact.last_name}
                  </h4>
                  {contact.title && (
                    <p className="text-xs text-gray-500">{contact.title}</p>
                  )}
                </div>
                {contact.member_status && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      formatMemberStatus(contact.member_status).color
                    }`}
                  >
                    {formatMemberStatus(contact.member_status).label}
                  </span>
                )}
              </div>

              {/* Contact Info */}
              <div className="space-y-1.5 text-sm">
                {contact.email && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <svg
                      className="w-4 h-4 text-gray-400 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="truncate">{contact.email}</span>
                  </div>
                )}
                {contact.phone_number && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <svg
                      className="w-4 h-4 text-gray-400 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    <span>{contact.phone_number}</span>
                  </div>
                )}
                {(contact.city || contact.state) && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <svg
                      className="w-4 h-4 text-gray-400 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span>
                      {[contact.city, contact.state].filter(Boolean).join(", ")}
                    </span>
                  </div>
                )}
              </div>

              {/* Lifecycle Stage */}
              {contact.lifecycle_stage && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Stage:</span>
                  <span className="text-xs font-medium text-gray-700">
                    {formatLifecycleStage(contact.lifecycle_stage)}
                  </span>
                </div>
              )}

              {/* Tags */}
              {contact.tags && contact.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {contact.tags.slice(0, 4).map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                  {contact.tags.length > 4 && (
                    <span className="text-xs text-gray-500">
                      +{contact.tags.length - 4} more
                    </span>
                  )}
                </div>
              )}

              {/* Footer - View Profile Link */}
              <div className="pt-2 border-t border-gray-100">
                <Link
                  to={`/contacts/${contact.id}`}
                  state={fromTaskId ? { fromTaskId } : undefined}
                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  View Full Profile
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500 text-center py-4">
              Contact not found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactQuickView;
